import { Link } from 'react-router-dom';

import {
  DataTable,
  Paginator,
  SkeletonLoader,
  FilterBar,
  PageActions,
  ExportButton,
  naBody,
} from 'components';

import { humanReadable, humanReadableGermanDate } from 'utils';
import { locales, routes, ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import columns from './columns';
import { ListTwoUiProps, ListTwoTicket } from './ListTwo.types';

import './list-two.scss';

function TypeBodyTemplate(row: ListTwoTicket) {
  return (
    <span
      className={`colored-circle colored-circle--${row.type.toLowerCase()}`}
    >
      {row.type}
    </span>
  );
}

function dateBodyTemplate(field: string) {
  return function (row: ListTwoTicket) {
    if (row[field as keyof ListTwoTicket])
      return humanReadable(row[field as keyof ListTwoTicket] as string);

    return 'N/A';
  };
}

function affectedDateTemplate(row: ListTwoTicket) {
  if (row.affectedDate) return row.affectedDate;

  return 'N/A';
}

function LinkBodyTemplate(row: ListTwoTicket) {
  return (
    <Link to={`${routes.listTwo.index}/${row.id}`}>
      <div className="list-two-tickets__icon list-two-tickets__icon--arrow" />
    </Link>
  );
}

export default function ListTwoUi({
  isExporting,
  isLoading,
  filter,
  options,
  totalRecords,
  data: { tickets },
  handlers,
}: ListTwoUiProps) {
  return (
    <div className="list-two-tickets">
      <PageActions>
        <ExportButton
          disabled={isLoading || !tickets || tickets.length === 0}
          onClick={handlers.handleDownload}
          loading={isExporting}
        />
      </PageActions>

      <FilterBar
        loading={isLoading}
        onReset={handlers.handleClearAllFilters}
        search={{
          inputs: [
            {
              name: 'employeeId',
              placeholder: locales.general.employeeID,
              value: filter.employeeId || '',
              fieldWidth: 12,
            },
            {
              name: 'personId',
              placeholder: locales.general.personID,
              value: filter.personId || '',
              fieldWidth: 12,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 4,
        }}
        filters={[
          {
            kind: 'dropdown',
            name: 'ticketType',
            placeholder: locales.general.filterByType,
            fieldWidth: 14,
            value: filter.ticketType,
            options: options.ticketTypes,
            onChange: handlers.handleTicketTypeChange,
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'status',
            placeholder: locales.general.filterByStatus,
            fieldWidth: 18,
            value: filter.ticketStatus,
            options: options.ticketStatuses,
            onChange: handlers.handleTicketStatusChange,
            width: 3,
          },
          {
            kind: 'date',
            label: 'Date range',
            value: filter.dates,
            fieldWidth: 24,
            onChange: handlers.handleDateChange,
            width: 2,
          },
        ]}
      />

      {isLoading ? (
        <SkeletonLoader height="10rem" />
      ) : (
        <DataTable
          sortMode="multiple"
          totalRecords={totalRecords}
          columns={columns.map((column) => {
            if (column.field === 'type') {
              return {
                ...column,
                body: TypeBodyTemplate,
              };
            }

            if (
              column.field === 'createdDate' ||
              column.field === 'updatedDate'
            ) {
              return {
                ...column,
                body: dateBodyTemplate(column.field),
              };
            }

            if (column.field === 'affectedDate')
              return {
                ...column,
                body: affectedDateTemplate,
              };

            if (column.field === 'link') {
              return {
                ...column,
                body: LinkBodyTemplate,
              };
            }

            return {
              ...column,
              body: naBody(column.field),
            };
          })}
          value={tickets.map((ticket) => ({
            ...ticket,
            affectedDate: humanReadableGermanDate(
              ticket.affectedDate as string,
              'DD/MM/YYYY'
            ),
          }))}
        />
      )}

      {tickets?.length > 0 &&
        (isLoading ? (
          <SkeletonLoader height="3rem" width="40rem" />
        ) : (
          <Paginator
            first={filter.first}
            rows={filter.rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={handlers.handlePageChange}
          />
        ))}
    </div>
  );
}
